/**
 * Sources — reference area for chat citation sources.
 *
 * Layout (single responsive path, evidence in
 * docs/dnacke/SOTA_CITATION_UX_VISUAL.md):
 *   - A collapsed SOURCES BAR (stacked type icons + "N Quellen · M Passagen")
 *     that expands on click; auto-expanded from 8 passages up. An always-open
 *     source block with few citations measurably lowers trust.
 *   - Per document a CARD: number badge (same number/color as the inline chip)
 *     + type icon + title + open action.
 *   - Below each card header ONE ROW PER CITED PASSAGE (a source's `locators`
 *     are expanded, see expandGroupRows): "S. 16-17 · Abschnitt", the FIRST
 *     row shows its quote inline (2-line clamp), further rows keep the quote
 *     on hover. Row click jumps into the in-app viewer at the passage.
 *
 * Grouping: sources group by the INTEGER PREFIX of citation_id; groups without
 * a citation_id fall back to document_id / url / title grouping.
 *
 * Chip coupling: chip hover highlights rows via activeCitation; chip CLICK
 * sends a reveal request (CitationContext) that expands the collapsed section,
 * scrolls to id="source-card-{citation_id}" and flashes it.
 */

import {
  Box,
  ButtonBase,
  Collapse,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LinkIcon from '@mui/icons-material/Link';
import React, { useState } from 'react';
import { documentTypeIcon, resolveTypeHint } from './documentTypeIcon';
import { useTranslation } from 'react-i18next';
import { ChatSource, SourceLocator } from '../../api/chatTypes';
import { viewOriginalDocument } from '../../utils/documents/documentHelpers';
import { isUserUploadedDocument } from '../../utils/documents/documentTypeUtils';
import { isSafeUrl, isExternalUrl } from '../../utils/security/linkSafety';
import { FAQDetailDialog } from '../faqs/FAQDetailDialog';
import { type TextSourceTarget } from './TextSourceDrawer';
import { type PdfPassage, type PdfPassageTarget } from './PdfPassageDrawer';
import { type ImagePassageTarget } from './ImagePassageDrawer';
import { type XlsxPassageTarget } from './XlsxPassageDrawer';
import { isTextLikeSource } from '../../utils/documents/textSource';
import { usePermissions } from '../../hooks/permissions/usePermissions';
import { log } from '../../utils/errors/logger';
import {
  buildLocatorFragment,
  buildWebTextFragment,
  formatPositions,
  formatSourceLocator,
  formatTimestamp,
} from './sourceLocator';
import { useCitationContext } from './CitationContext';
import { locatorPassages, sourceLocators } from './passageBinding';

// ---------------------------------------------------------------------------
// Global CSS for the chip-flash animation. Injected once per page.
// ---------------------------------------------------------------------------
const FLASH_STYLE_ID = 'citation-active-flash-style';
if (typeof document !== 'undefined' && !document.getElementById(FLASH_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = FLASH_STYLE_ID;
  style.textContent = `
    @keyframes citationFlash {
      0%   { background-color: rgba(0,48,100,0.00); }
      20%  { background-color: rgba(0,48,100,0.14); }
      80%  { background-color: rgba(0,48,100,0.14); }
      100% { background-color: rgba(0,48,100,0.00); }
    }
    .citation-active-flash {
      animation: citationFlash 0.8s ease-in-out;
    }
  `;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Correct SAP Discovery Center URLs (carried over from original Sources.tsx). */
function getCorrectUrl(url: string): string {
  if (!url) {
    return '';
  }
  if (url.includes('discovery-center.cloud.sap/serviceCatalog/')) {
    const uuidMatch = url.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    if (uuidMatch?.[1]) {
      return `https://discovery-center.cloud.sap/protected/index.html#/ai-feature/${uuidMatch[1]}/`;
    }
  }
  return url;
}

/**
 * Defensive title cleanup: strip ".bin" extension from hex-only filenames
 * (old cached messages). Real human titles are returned unchanged.
 */
function cleanTitle(title: string | null | undefined): string {
  if (!title) {
    return '';
  }
  const binMatch = title.match(/^([0-9a-f]{8,})\.(bin)$/i);
  if (binMatch) {
    return binMatch[1];
  }
  return title;
}

/**
 * Navigation/boilerplate segment labels that carry no meaningful document
 * content. When a web section_path ends with one of these, we look for a
 * better (earlier) heading instead.
 */
const BOILERPLATE_SEGMENTS = new Set([
  'related content',
  'footnotes',
  'company',
  'products',
  'help and security',
  'navigation',
  'see also',
  'references',
  'external links',
  'contents',
  'table of contents',
]);

/**
 * Parse a section_path into a numeric prefix and a title suffix.
 *
 * PDF/DOCX examples (numbered sections):
 *   "1.1 Ziel und Geltungsbereich"   -> num="1.1", title="Ziel und Geltungsbereich"
 *   "2 Sofortmaßnahmen"              -> num="2",   title="Sofortmaßnahmen"
 *   "3 Setup > 3.2 OAuth"            -> num="3.2", title="OAuth"  (take last segment)
 *
 * Web/URL examples (no numbers, multi-level path):
 *   "Page Title > A new data retention policy"    -> num="", title="A new data retention policy"
 *   "Page Title > Claude Fable 5 > Footnotes"     -> num="", title="Claude Fable 5" (skip boilerplate)
 *   "Page Title > Navigation > Company"           -> num="", title="Page Title" (all boilerplate -> last)
 *
 * The FIRST segment is treated as the document title root and is NOT used as
 * the section heading (it duplicates the group header). We derive the heading
 * from the remaining segments, preferring the most specific non-boilerplate one.
 */
function parseSectionPath(sectionPath: string | null | undefined): { num: string; title: string } {
  if (!sectionPath?.trim()) {
    return { num: '', title: '' };
  }
  const parts = sectionPath.split(/ > /).map((s) => s.trim()).filter(Boolean);

  // For a single-segment path (no ">") treat it the same way as before: check for
  // a numeric prefix and extract heading.
  if (parts.length === 1) {
    const only = parts[0];
    const m = only.match(/^(\d[\d.]*\.?)\s+(.+)$/);
    if (m) {
      return { num: m[1].replace(/\.$/, ''), title: m[2].trim() };
    }
    return { num: '', title: only };
  }

  // For multi-segment paths: the last segment is the most specific. Try it first.
  // Walk from the end and pick the first non-boilerplate segment that is NOT the
  // document-title root (first segment).
  const root = parts[0];
  const candidates = parts.slice(1); // exclude the document title root

  // Try from most-specific (last) to least-specific (first after root).
  let chosenTitle = '';
  let chosenNum = '';
  for (let i = candidates.length - 1; i >= 0; i--) {
    const seg = candidates[i];
    if (BOILERPLATE_SEGMENTS.has(seg.toLowerCase())) {
      continue; // skip generic navigation labels
    }
    // Check for numeric prefix (PDF-style numbered section).
    const m = seg.match(/^(\d[\d.]*\.?)\s+(.+)$/);
    if (m) {
      chosenNum = m[1].replace(/\.$/, '');
      chosenTitle = m[2].trim();
    } else {
      chosenTitle = seg;
    }
    break;
  }

  // All candidates were boilerplate: fall back to root (document title) as last resort,
  // or the last segment verbatim if root is also uninformative.
  if (!chosenTitle) {
    const last = candidates[candidates.length - 1];
    chosenTitle = last || root;
  }

  return { num: chosenNum, title: chosenTitle };
}

/**
 * Build a compact page abbreviation string for a locator.
 * Returns the raw positions string (e.g. "2", "3-5") or ''.
 */
function locatorPageStr(locator: SourceLocator): string {
  const paged =
    locator.loc_kind === 'page' ||
    locator.loc_kind === 'slide' ||
    locator.loc_kind === 'region' ||
    // XLSX: a known worksheet name replaces the numeric "Sheet N" label.
    (locator.loc_kind === 'sheet' && !locator.sheet_name && !locator.section_path);
  if (!paged) {
    return '';
  }
  const numbers = (locator.positions ?? []).filter(
    (n): n is number => typeof n === 'number' && Number.isFinite(n)
  );
  if (numbers.length === 0) {
    return '';
  }
  return formatPositions(numbers);
}

/**
 * Extra location detail beyond page/section: the cited cell range of a
 * spreadsheet chunk, or the cited time range of a transcript segment.
 */
function locatorDetailStr(locator: SourceLocator): string {
  if (locator.loc_kind === 'sheet') {
    return locator.a1_range?.trim() ?? '';
  }
  if (locator.loc_kind === 'timestamp' && typeof locator.t_start === 'number') {
    const start = formatTimestamp(locator.t_start);
    return typeof locator.t_end === 'number' && locator.t_end > locator.t_start
      ? `${start}–${formatTimestamp(locator.t_end)}`
      : start;
  }
  return '';
}

/**
 * Compare two citation ids ("1", "1.2", "1.10", "2.1") in natural numeric,
 * document order: the bare doc id "1" sorts before its sections "1.1"/"1.2",
 * and "1.2" sorts before "1.10" (numeric, not lexicographic).
 */
function compareCitationIds(a: string, b: string): number {
  const pa = a.split('.').map((p) => parseInt(p, 10));
  const pb = b.split('.').map((p) => parseInt(p, 10));
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const na = pa[i] ?? -1; // shorter id (e.g. "1") sorts before "1.1"
    const nb = pb[i] ?? -1;
    if (na !== nb) {
      return na - nb;
    }
  }
  return 0;
}

/**
 * Extract the integer document-group key from a citation_id.
 * "1"   -> "1"
 * "1.2" -> "1"
 * "2.1" -> "2"
 * 1     -> "1"   (older messages persist citation_id as a number)
 * null/undefined/"" -> null
 *
 * Coerces to string defensively: stored conversations from before the two-tier
 * change carry a numeric citation_id, and calling .split on a number throws.
 */
function docGroupKey(citationId: string | number | null | undefined): string | null {
  if (citationId === null || citationId === undefined || citationId === '') {
    return null;
  }
  const prefix = String(citationId).split('.')[0];
  return prefix || null;
}

/**
 * Group an array of ChatSource entries into two-tier document groups.
 *
 * Primary key: the integer prefix of citation_id (e.g. "1" for "1.1", "1.2").
 * Fallback key (for sources without citation_id): document_id > url > title.
 *
 * Returns an ordered array of { groupDocKey, docNumber, sources } where:
 *   - groupDocKey is the stable map key (string)
 *   - docNumber is the display badge label (the integer prefix string, e.g. "1")
 *   - sources is the ordered ChatSource[] for that group
 */
interface DocumentGroup {
  groupDocKey: string;
  /** The integer prefix string to show on the header badge, e.g. "1", "2". */
  docNumber: string;
  sources: ChatSource[];
}

function groupSourcesByDocument(sources: ChatSource[]): DocumentGroup[] {
  const map = new Map<string, { docNumber: string; sources: ChatSource[] }>();
  const order: string[] = [];

  for (const source of sources) {
    const numPrefix = docGroupKey(source.citation_id);
    let key: string;
    let docNumber: string;

    if (numPrefix !== null) {
      // Two-tier: group by integer prefix of citation_id.
      key = `citnum:${numPrefix}`;
      docNumber = numPrefix;
    } else if (source.document_id) {
      key = `doc:${source.document_id}`;
      docNumber = '';
    } else if (source.url) {
      key = source.url.startsWith('/document/view/')
        ? `doc:${source.url.replace('/document/view/', '')}`
        : `url:${source.url}`;
      docNumber = '';
    } else {
      key = `title:${source.title ?? ''}`;
      docNumber = '';
    }

    const existing = map.get(key);
    if (existing) {
      existing.sources.push(source);
    } else {
      map.set(key, { docNumber, sources: [source] });
      order.push(key);
    }
  }

  return order.map((key) => {
    const entry = map.get(key)!;
    return { groupDocKey: key, docNumber: entry.docNumber, sources: entry.sources };
  });
}

/**
 * Drop the bare whole-document citation row ("N", no dot) from a group when
 * that same document is ALSO cited for one or more specific sections ("N.x").
 * In that case the bare "N" row carries no section/locator of its own and
 * renders an empty "–" title, duplicating the document header — it is pure
 * noise next to the meaningful section rows. A group cited ONLY as a whole
 * (no "N.x" rows) keeps its single "N" row.
 */
function dropRedundantWholeDocRow(groupSources: ChatSource[]): ChatSource[] {
  const hasSectionRow = groupSources.some(
    (s) =>
      s.citation_id !== null &&
      s.citation_id !== undefined &&
      String(s.citation_id).includes('.')
  );
  if (!hasSectionRow) {
    return groupSources;
  }
  return groupSources.filter(
    (s) =>
      s.citation_id === null ||
      s.citation_id === undefined ||
      String(s.citation_id).includes('.')
  );
}

/**
 * Office formats that have a server-side PDF preview (converted on demand by
 * the backend via headless LibreOffice). These open in the in-app PDF passage
 * viewer with `src: 'preview'`.
 */
const OFFICE_FILE_TYPES = new Set([
  'docx', 'doc', 'odt', 'rtf',
  'pptx', 'ppt', 'odp',
  'xlsx', 'xls', 'ods',
]);

export function isOfficeSource(source: ChatSource): boolean {
  const fileType = (source.file_type ?? '').toLowerCase();
  if (OFFICE_FILE_TYPES.has(fileType)) {
    return true;
  }
  const title = (source.title ?? '').toLowerCase();
  const ext = title.includes('.') ? title.slice(title.lastIndexOf('.') + 1) : '';
  return OFFICE_FILE_TYPES.has(ext);
}

/**
 * Strict .xlsx sources open in the in-app Univer grid viewer (cell-precise
 * range jump). Other spreadsheet formats (.xls/.ods) stay on the LibreOffice
 * PDF preview path — the client-side parser reads only .xlsx.
 */
export function isXlsxGridSource(source: ChatSource): boolean {
  const fileType = (source.file_type ?? '').toLowerCase();
  if (fileType === 'xlsx') {
    return true;
  }
  return (source.title ?? '').toLowerCase().endsWith('.xlsx');
}

/** Image formats shown in the in-app image viewer (with OCR region overlay). */
const IMAGE_FILE_TYPES = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp']);

export function isImageSource(source: ChatSource): boolean {
  const fileType = (source.file_type ?? '').toLowerCase();
  if (IMAGE_FILE_TYPES.has(fileType)) {
    return true;
  }
  const title = (source.title ?? '').toLowerCase();
  const ext = title.includes('.') ? title.slice(title.lastIndexOf('.') + 1) : '';
  return IMAGE_FILE_TYPES.has(ext);
}

/** One rendered reference row: a (source, locator) pair. */
interface LocatorRow {
  source: ChatSource;
  citationId: string;
  /** Unique key per row within a group (citation id + locator index). */
  rowKey: string;
  /** True on the first row of a citation id — carries the source-card DOM anchor. */
  isAnchor: boolean;
  locator: SourceLocator | null;
}

function locatorDedupKey(locator: SourceLocator | null): string {
  if (!locator) {
    return 'none';
  }
  return [
    locator.loc_kind,
    (locator.positions ?? []).join('-'),
    locator.section_path ?? '',
    locator.a1_range ?? '',
    locator.t_start ?? '',
    // Two passages of one page with different quotes are two rows.
    locator.excerpt ?? '',
  ].join('|');
}

/**
 * One row per highlight window: a locator whose attribution found several
 * supporting windows on the same page (a checklist answered from ten rows)
 * becomes one derived locator per window, each carrying its own quote and
 * anchoring context, so the reference list and the "N Passagen" count match
 * what the viewer marks.
 */
function expandQuoteWindows(locator: SourceLocator | null): (SourceLocator | null)[] {
  const quotes = locator?.quotes ?? [];
  if (!locator || quotes.length <= 1) {
    return [locator];
  }
  return quotes.map((q) => ({
    ...locator,
    excerpt: q.text,
    quote_prefix: q.prefix ?? null,
    quote_suffix: q.suffix ?? null,
    quotes: [q],
  }));
}

/**
 * Expand a document group into one reference row per cited passage.
 *
 * The backend sends ONE ChatSource per document whose `locators` array holds
 * every contributing chunk's page/section (best match first). Rendering one row
 * per ChatSource collapsed a multi-passage citation to a single line, losing
 * the per-page jump links. Expanding (source x locator) yields one row per
 * cited passage instead. Rows are deduped on (kind, positions, section) and
 * ordered by first position so the reference list reads in document order.
 */
function expandGroupRows(groupSources: ChatSource[]): LocatorRow[] {
  const sorted = [...dropRedundantWholeDocRow(groupSources)].sort((a, b) =>
    compareCitationIds(String(a.citation_id ?? ''), String(b.citation_id ?? ''))
  );
  const rows: LocatorRow[] = [];
  sorted.forEach((source, srcIdx) => {
    const citationId =
      source.citation_id !== null && source.citation_id !== undefined
        ? String(source.citation_id)
        : String(srcIdx + 1);
    const locs: (SourceLocator | null)[] = (
      source.locators && source.locators.length > 0
        ? [...source.locators]
        : [source.locator ?? null]
    ).flatMap(expandQuoteWindows);
    const seen = new Set<string>();
    const deduped = locs.filter((l) => {
      const key = locatorDedupKey(l);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
    deduped.sort(
      (a, b) =>
        (a?.positions?.[0] ?? Number.MAX_SAFE_INTEGER) -
        (b?.positions?.[0] ?? Number.MAX_SAFE_INTEGER)
    );
    deduped.forEach((locator, locIdx) => {
      rows.push({
        source,
        citationId,
        rowKey: `${citationId}:${locIdx}`,
        isAnchor: locIdx === 0,
        locator,
      });
    });
  });
  return rows;
}

/**
 * Whether a chunk has a usable, displayable locator on ANY of its passages
 * (not only the best-match first one).
 */
function hasUsableLocator(source: ChatSource): boolean {
  const candidates: (SourceLocator | null | undefined)[] =
    source.locators && source.locators.length > 0 ? source.locators : [source.locator];
  return candidates.some(
    (l) =>
      l !== null &&
      l !== undefined &&
      // A quote-only locator (synthetic "text" kind for plain-text chunks) is
      // usable too: the quote itself is the row's display and jump anchor.
      (formatSourceLocator(l) !== null || Boolean(l.excerpt))
  );
}

/**
 * Whether a document group has at least one chunk with a usable locator.
 */
function groupHasLocators(groupSources: ChatSource[]): boolean {
  return groupSources.some(hasUsableLocator);
}

/**
 * Whether the per-section reference table should be shown for a document group.
 *
 * Show it when EITHER:
 *  - any row has a usable locator (page/section/sheet/line to label), OR
 *  - the document was cited for one or more SPECIFIC sections (ids with a
 *    sub-index like "1.2"), even when those sections carry no page/heading
 *    label — the references themselves are meaningful and each row still has an
 *    excerpt to show. (Docling sometimes extracts no headings from a DOCX/URL,
 *    leaving section locators blank; suppressing the whole table then hides
 *    citations the user actually made.)
 *
 * Only a pure whole-document citation (just the bare "N" entry, no sections and
 * no locators) renders header-only. A single row WITH a locator still shows the
 * table: unlike the header it carries the page/section line and the per-page
 * PDF jump action, which is the whole point of the citation feature.
 */
export function groupShowsTable(groupSources: ChatSource[]): boolean {
  if (groupHasLocators(groupSources)) {
    return true;
  }
  // Count distinct per-section citations (citation_id contains a ".").
  const sectionIds = new Set<string>();
  for (const s of groupSources) {
    if (s.citation_id !== null && s.citation_id !== undefined) {
      const id = String(s.citation_id);
      if (id.includes('.')) {
        sectionIds.add(id);
      }
    }
  }
  return sectionIds.size > 0;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/** An uploaded PDF (by file type or name); Office documents use the preview PDF. */
function isPdfSource(source: ChatSource): boolean {
  return (
    (source.file_type ?? '').toLowerCase() === 'pdf' ||
    (source.title ?? '').toLowerCase().endsWith('.pdf')
  );
}

/**
 * One viewer passage for a cited locator. Page/region locators map directly;
 * PPTX slide numbers map 1:1 onto preview pages. Section/sheet locators carry
 * no page, so the drawer scans pages for the quote instead. Region locators
 * additionally carry normalized bboxes (scanned pages) which the drawer
 * overlays when text-layer quote anchoring finds nothing.
 */
function toPdfPassage(
  source: ChatSource,
  locator: SourceLocator | null | undefined,
  label: string | null | undefined
): PdfPassage {
  const kind = locator?.loc_kind;
  return {
    pages: kind === 'page' || kind === 'slide' || kind === 'region' ? locator?.positions ?? null : null,
    quote: locator?.excerpt ?? source.excerpt ?? null,
    quotePrefix: locator?.quote_prefix ?? null,
    quoteSuffix: locator?.quote_suffix ?? null,
    regions: locator?.regions ?? null,
    label: label ?? null,
  };
}

/** Row label "1.2" (several passages) or "1" (single passage) for a document. */
function passageRowLabel(docNumber: string | null | undefined, rowCount: number, rowIdx: number, citationId: string): string {
  if (!docNumber) {
    return citationId;
  }
  return rowCount > 1 ? `${docNumber}.${rowIdx + 1}` : String(docNumber);
}

interface SourcesProps {
  sources: ChatSource[];
  /** Open a text/manual source in the shared in-app preview drawer (owned by
   *  ChatMessage so in-text citation links and source rows share one drawer). */
  onOpenTextSource?: (target: TextSourceTarget) => void;
  /** Open a PDF source in the in-app passage viewer (owned by ChatMessage),
   *  jumped to the cited page with the cited passage highlighted. */
  onOpenPdfPassage?: (target: PdfPassageTarget) => void;
  /** Open an image source in the in-app image viewer (owned by ChatMessage),
   *  with the cited OCR regions highlighted as an overlay. */
  onOpenImagePassage?: (target: ImagePassageTarget) => void;
  /** Open an .xlsx source in the in-app grid viewer (owned by ChatMessage),
   *  jumped to the cited worksheet and cell range. */
  onOpenXlsxPassage?: (target: XlsxPassageTarget) => void;
}

export const Sources: React.FC<SourcesProps> = ({
  sources,
  onOpenTextSource,
  onOpenPdfPassage,
  onOpenImagePassage,
  onOpenXlsxPassage,
}) => {
  const { t } = useTranslation();
  const { user } = usePermissions();
  const { activeCitation, revealRequest } = useCitationContext();

  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [selectedFaqId, setSelectedFaqId] = useState<string | null>(null);
  // Collapsed by default; a user toggle overrides it for this message.
  const [expanded, setExpanded] = useState(false);
  // Per-document passage rows: collapsed by default (header stays visible).
  // docOpenDefault flips to true when a citation chip reveals the section, so
  // every document opens; explicit toggles are kept per document.
  const [docOpenDefault, setDocOpenDefault] = useState(false);
  const [docOpenState, setDocOpenState] = useState<ReadonlyMap<string, boolean>>(() => new Map());
  const isDocOpen = (key: string) => docOpenState.get(key) ?? docOpenDefault;
  const toggleDoc = (key: string) => {
    setDocOpenState((prev) => {
      const next = new Map(prev);
      next.set(key, !(prev.get(key) ?? docOpenDefault));
      return next;
    });
  };

  // Chip CLICK in the answer text: expand the collapsed sources section and
  // every collapsed card first, then scroll to and flash the citation's card
  // once the collapse transition has revealed it.
  React.useEffect(() => {
    if (!revealRequest) {
      return undefined;
    }
    setExpanded(true);
    setDocOpenDefault(true);
    setDocOpenState(new Map());
    const timer = setTimeout(() => {
      const el = document.getElementById(`source-card-${revealRequest.id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        el.classList.add('citation-active-flash');
        setTimeout(() => el.classList.remove('citation-active-flash'), 800);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [revealRequest]);

  if (sources.length === 0) {
    return null;
  }

  const documentGroups = groupSourcesByDocument(sources);
  const docCount = documentGroups.length;
  const passageCount = documentGroups.reduce(
    (n, g) => n + (groupShowsTable(g.sources) ? expandGroupRows(g.sources).length : 0),
    0
  );
  const effectiveExpanded = expanded;

  // ---------------------------------------------------------------------------
  // Document open handler — full security + FAQ logic + Bug D URL fix
  // ---------------------------------------------------------------------------
  const handleDocumentClick = (
    source: ChatSource,
    jumpLocator: SourceLocator | null | undefined = source.locator,
    passageLabel?: string,
    allPassages?: PdfPassage[]
  ) => {
    const sourceType = source.source_type ?? '';
    const fragment = buildLocatorFragment(jumpLocator);
    log.debug('Source clicked', {
      title: source.title,
      source_type: sourceType,
      document_id: source.document_id,
      url: source.url,
      fragment,
      hasDocId: Boolean(source.document_id),
    });

    // FAQ: open dialog.
    if (source.source_type === 'faq' && source.document_id) {
      log.user.action('faq_opened', 'Sources', {
        faq_id: source.document_id,
        faq_title: source.title,
      });
      setSelectedFaqId(source.document_id);
      setFaqDialogOpen(true);
      return;
    }

    // Text / manual ("Textinhalt") sources: render the content IN-APP in a side
    // drawer (with the cited passage highlighted) instead of opening a raw-text
    // browser tab. Manual sources have no file to open, so the old path broke
    // for them. Only plain-text sources take this branch; PDF/URL/Office docs
    // fall through to their existing open behavior.
    if (isTextLikeSource(source) && source.document_id && onOpenTextSource) {
      log.user.action('text_source_previewed', 'Sources', {
        document_id: source.document_id,
        document_title: source.title,
      });
      onOpenTextSource({
        documentId: source.document_id,
        title: source.title,
        // Per-chunk mode fills source.excerpt; in per-document mode the
        // (attribution-focused) quote lives on the clicked locator.
        excerpt: jumpLocator?.excerpt ?? source.excerpt,
      });
      return;
    }

    // Fix 3 + Fix 2: URL/web sources (external http(s) URL) open the original
    // website in a new tab. This branch MUST run before the uploaded-document
    // branches so that a web source that also carries a document_id (edge case)
    // is still opened as a website, not routed through the API viewer.
    // PDFs have url="/document/view/..." (not http), so they fall through.
    if (source.url && (source.url.startsWith('https://') || source.url.startsWith('http://'))) {
      const correctedUrl = getCorrectUrl(source.url);
      if (isSafeUrl(correctedUrl) && isExternalUrl(correctedUrl)) {
        // B4: append a Text Fragment so the browser scrolls to and highlights
        // the cited passage on the live page. Best-effort — ignored by the
        // browser (and harmless) if the page text has drifted, or if the URL
        // already carries its own fragment (don't clobber it).
        const textFragment = correctedUrl.includes('#')
          ? ''
          : buildWebTextFragment(jumpLocator?.excerpt ?? source.excerpt);
        log.user.action('external_link_opened', 'Sources', {
          url: correctedUrl,
          source_title: source.title,
          highlighted: Boolean(textFragment),
        });
        window.open(correctedUrl + textFragment, '_blank', 'noopener,noreferrer');
        return;
      }
      log.warn('Source URL failed safety check', { url: source.url?.substring(0, 80) });
      return;
    }

    // Uploaded PDF: open the in-app passage viewer, jumped to the cited page
    // with the cited passage highlighted (quote-anchored in the text layer).
    // Falls back to the plain new-tab #page=N open when the drawer is not
    // wired (older call sites) — and the drawer itself offers an external-open
    // escape hatch.
    // .xlsx: native in-app grid viewer with cell-precise range jump (the cited
    // worksheet + A1 range come from index-time provenance). Other Office
    // formats fall through to the PDF preview drawer below.
    if (isXlsxGridSource(source) && source.document_id && onOpenXlsxPassage) {
      onOpenXlsxPassage({
        documentId: source.document_id,
        title: source.title,
        sheetName: jumpLocator?.sheet_name ?? jumpLocator?.section_path ?? null,
        a1Range: jumpLocator?.a1_range ?? null,
      });
      return;
    }

    const isPdf = isPdfSource(source);
    const isOffice = isOfficeSource(source);
    if ((isPdf || isOffice) && source.document_id && onOpenPdfPassage) {
      log.user.action('pdf_passage_viewed', 'Sources', {
        document_id: source.document_id,
        document_title: source.title,
        page: jumpLocator?.positions?.[0],
        src: isOffice ? 'preview' : 'original',
        passages: allPassages?.length ?? 1,
      });
      // A document-header click carries EVERY cited passage of the document
      // so the viewer marks and labels all of them (1.1, 1.2, ...); a row
      // click carries just its own passage.
      onOpenPdfPassage({
        documentId: source.document_id,
        title: source.title,
        ...toPdfPassage(source, jumpLocator, passageLabel),
        src: isOffice ? 'preview' : 'original',
        passages: allPassages && allPassages.length > 1 ? allPassages : null,
      });
      return;
    }

    // Image sources (PNG/JPG): open the in-app image viewer. When the cited
    // chunk carries OCR bbox provenance (regions), the cited text is
    // highlighted as a percent-positioned overlay on the image.
    if (isImageSource(source) && source.document_id && onOpenImagePassage) {
      log.user.action('image_passage_viewed', 'Sources', {
        document_id: source.document_id,
        document_title: source.title,
        has_regions: Boolean(jumpLocator?.regions?.length),
      });
      // Every cited window of the image is handed over, not just the clicked
      // one: a chart cites its title and its values as separate chunks, each
      // carrying its own boxes, and marking only one left the rest blank.
      const imagePassages = sourceLocators(source).map((l) => ({
        regions: l.regions ?? null,
        label: passageLabel ?? null,
        primary: l === jumpLocator,
      }));
      onOpenImagePassage({
        documentId: source.document_id,
        title: source.title,
        regions: jumpLocator?.regions ?? null,
        label: passageLabel ?? null,
        passages: imagePassages.length > 0 ? imagePassages : null,
      });
      return;
    }

    // Fix 2: Uploaded document — prefer document_id (raw id) so viewOriginalDocument
    // builds the correct /api/documents/document/view/{id}#page=N URL.
    // Covers source_types: "document", "file", "custom", "manual".
    if (isUserUploadedDocument(sourceType) && source.document_id) {
      log.user.action('document_viewed', 'Sources', {
        document_id: source.document_id,
        document_type: sourceType,
        document_title: source.title,
      });
      void viewOriginalDocument(source.document_id, fragment || undefined);
      return;
    }

    // Fix 2 fallback: uploaded document identified by "/document/view/{id}" URL
    // when document_id is absent (older persisted messages). viewOriginalDocument
    // calls extractDocumentId internally so no double-prefix occurs.
    if (source.url?.startsWith('/document/view/')) {
      log.user.action('document_viewed', 'Sources', {
        url: source.url,
        document_title: source.title,
      });
      void viewOriginalDocument(source.url, fragment || undefined);
      return;
    }

    // Any remaining URL (relative or corrected external — fallback).
    if (source.url) {
      const correctedUrl = getCorrectUrl(source.url);
      // B4: highlight the cited passage on the live page (best-effort; skip if
      // the URL already has a fragment). Only meaningful for http(s) targets.
      const textFragment =
        correctedUrl.includes('#') || !/^https?:\/\//.test(correctedUrl)
          ? ''
          : buildWebTextFragment(source.excerpt);
      log.user.action('external_link_opened', 'Sources', {
        url: source.url,
        source_title: source.title,
        highlighted: Boolean(textFragment),
      });
      window.open(correctedUrl + textFragment, '_blank', 'noopener,noreferrer');
      return;
    }

    log.warn('Source click handler - no action matched', {
      source_type: source.source_type,
      has_document_id: Boolean(source.document_id),
      has_url: Boolean(source.url),
    });
  };

  // The header's open-in-new icon ALWAYS opens the ORIGINAL document in a new
  // tab — never an in-app viewer. The in-app previews stay on the title and
  // the passage rows; this button is the deliberate escape hatch to the file
  // itself (e.g. download the .docx behind a PDF preview).
  const handleOpenOriginal = (source: ChatSource) => {
    // FAQs have no original file: keep the FAQ dialog.
    if (source.source_type === 'faq' && source.document_id) {
      handleDocumentClick(source);
      return;
    }
    if (source.url && (source.url.startsWith('https://') || source.url.startsWith('http://'))) {
      const correctedUrl = getCorrectUrl(source.url);
      if (isSafeUrl(correctedUrl) && isExternalUrl(correctedUrl)) {
        log.user.action('external_link_opened', 'Sources', {
          url: correctedUrl,
          source_title: source.title,
        });
        window.open(correctedUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      log.warn('Source URL failed safety check', { url: source.url.substring(0, 80) });
      return;
    }
    if (source.document_id) {
      log.user.action('document_viewed', 'Sources', {
        document_id: source.document_id,
        document_title: source.title,
        via: 'open_original_button',
      });
      void viewOriginalDocument(source.document_id);
      return;
    }
    if (source.url?.startsWith('/document/view/')) {
      void viewOriginalDocument(source.url);
      return;
    }
    handleDocumentClick(source);
  };

  // ---------------------------------------------------------------------------
  // Render — collapsed sources bar (summary pill) above an expandable list of
  // per-document cards with one row per cited passage. Layout follows the
  // verified findings in docs/dnacke/SOTA_CITATION_UX_VISUAL.md: an always-open
  // source block with few citations lowers trust; hover-only quotes suppress
  // verification, so the first passage's quote is shown inline and further
  // passages keep the hover quote.
  // ---------------------------------------------------------------------------
  return (
    <Box sx={{ mt: 1.5 }}>
      {/* Sources bar: one neutral icon + "N Quellen · M Passagen"; click
          toggles the list. */}
      <ButtonBase
        onClick={() => setExpanded(!effectiveExpanded)}
        aria-expanded={effectiveExpanded}
        aria-label={t('chat.sources.title', { defaultValue: 'Quellen' })}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.25,
          py: '6px',
          borderRadius: '18px',
          border: '1px solid #dde3ea',
          backgroundColor: '#f5f7fa',
          '&:hover': { backgroundColor: '#eef2f7' },
          '&:focus-visible': { outline: '2px solid #003064', outlineOffset: '2px' },
        }}
      >
        {docCount === 1 ? (
          <DescriptionOutlinedIcon sx={{ fontSize: '1.05rem', color: '#5e6b7a' }} />
        ) : (
          <LibraryBooksOutlinedIcon sx={{ fontSize: '1.05rem', color: '#5e6b7a' }} />
        )}
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#3a4a5f' }}>
          {t('chat.sources.summary.documents', { count: docCount })}
          {passageCount > 0 && (
            <>
              {' \u00b7 '}
              {t('chat.sources.summary.passages', { count: passageCount })}
            </>
          )}
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: '1.1rem',
            color: '#6b7480',
            transition: 'transform 0.2s',
            transform: effectiveExpanded ? 'rotate(180deg)' : 'none',
          }}
        />
      </ButtonBase>

      <Collapse in={effectiveExpanded} timeout={250}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.25 }}>
          {documentGroups.map(({ groupDocKey, docNumber, sources: groupSources }) => {
            const representative = groupSources[0];
            const showTable = groupShowsTable(groupSources);
            const displayTitle = cleanTitle(representative.title);
            const rows = showTable ? expandGroupRows(groupSources) : [];
            const docCollapsed = !isDocOpen(groupDocKey);

            return (
              <Box
                key={groupDocKey}
                sx={{
                  border: '1px solid #dde3ea',
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 1px 3px rgba(16,24,40,0.06)',
                  transition: 'box-shadow 0.15s',
                  '&:hover': { boxShadow: '0 3px 10px rgba(16,24,40,0.10)' },
                }}
              >
                {/* ---- Document header ---- */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: '12px',
                    backgroundColor: '#f5f7fa',
                    borderBottom: rows.length > 0 ? '1px solid #dde3ea' : 'none',
                    gap: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: '1 1 auto' }}>
                    {/* Number badge — same number and color as the inline chip
                        so answer text and source list couple visually. Hidden
                        for header-only groups (no rows, no in-text chip). */}
                    {docNumber && showTable && (
                      <Box
                        component="span"
                        aria-label={`Document ${docNumber}`}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '22px',
                          height: '22px',
                          px: '5px',
                          borderRadius: '11px',
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          lineHeight: 1,
                          backgroundColor: '#003064',
                          color: '#ffffff',
                          flexShrink: 0,
                          userSelect: 'none',
                        }}
                      >
                        {docNumber}
                      </Box>
                    )}
                    {documentTypeIcon(resolveTypeHint(representative.source_type, representative.file_type, representative.url), representative.title, 40)}
                    <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                      <Tooltip title={t('chat.sources.openDocument', { defaultValue: 'Open document' })}>
                        <Typography
                          component="button"
                          type="button"
                          onClick={() => {
                            // Header click: open the viewer with every cited
                            // passage of this document marked and labelled.
                            if (rows.length > 1) {
                              const passages = rows.flatMap(({ source, locator, citationId }, rowIdx) =>
                                locatorPassages(
                                  source,
                                  locator,
                                  passageRowLabel(docNumber, rows.length, rowIdx, citationId),
                                  rowIdx === 0
                                )
                              );
                              handleDocumentClick(
                                rows[0].source,
                                rows[0].locator,
                                passageRowLabel(docNumber, rows.length, 0, rows[0].citationId),
                                passages
                              );
                              return;
                            }
                            handleDocumentClick(representative, representative.locator, docNumber ? String(docNumber) : undefined);
                          }}
                          sx={{
                            p: 0,
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            fontFamily: '"Roboto", sans-serif',
                            color: '#003064',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.4,
                            textAlign: 'left',
                            display: 'block',
                            width: '100%',
                            '&:hover': { textDecoration: 'underline' },
                            '&:focus-visible': { outline: '2px solid #003064', outlineOffset: '2px', borderRadius: '2px' },
                          }}
                        >
                          {displayTitle}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {rows.length > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ color: '#5e6b7a', whiteSpace: 'nowrap' }}
                      >
                        {t('chat.sources.summary.passages', { count: rows.length })}
                      </Typography>
                    )}
                    {rows.length > 0 && (
                      <IconButton
                        size="small"
                        onClick={() => toggleDoc(groupDocKey)}
                        aria-expanded={!docCollapsed}
                        aria-label={t(docCollapsed ? 'chat.sources.expandDocument' : 'chat.sources.collapseDocument', {
                          defaultValue: docCollapsed ? 'Fundstellen anzeigen' : 'Fundstellen ausblenden',
                          count: rows.length,
                        })}
                        sx={{
                          color: '#5e6b7a',
                          p: '5px',
                          '&:focus-visible': { outline: '2px solid #003064', outlineOffset: '2px' },
                        }}
                      >
                        <ExpandMoreIcon
                          sx={{
                            fontSize: '1.1rem',
                            transition: 'transform 0.2s',
                            transform: docCollapsed ? 'none' : 'rotate(180deg)',
                          }}
                        />
                      </IconButton>
                    )}
                    <Tooltip
                      title={t('chat.sources.openOriginal', {
                        defaultValue: 'Original in neuem Tab öffnen',
                      })}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleOpenOriginal(representative)}
                        aria-label={t('chat.sources.openOriginal', {
                          defaultValue: 'Original in neuem Tab öffnen',
                        })}
                        sx={{
                          color: '#003064',
                          p: '5px',
                          '&:hover': { backgroundColor: 'rgba(0,48,100,0.08)' },
                          '&:focus-visible': { outline: '2px solid #003064', outlineOffset: '2px' },
                        }}
                      >
                        <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* ---- Passage rows: one per cited passage. First row shows
                       its quote inline (clamped); further rows keep the quote
                       on hover. Row click jumps into the document viewer. ---- */}
                <Collapse in={!docCollapsed} timeout={200}>
                {rows.map(({ source, citationId, rowKey, isAnchor, locator }, rowIdx) => {
                  const rowLabel = passageRowLabel(docNumber, rows.length, rowIdx, citationId);
                  const pageStr = locator ? locatorPageStr(locator) : '';
                  const { num: sectionNum, title: sectionTitle } = parseSectionPath(locator?.section_path);
                  const fragment = buildLocatorFragment(locator);
                  const rowCanJump =
                    fragment !== '' ||
                    (Boolean(source.document_id) &&
                      ((isOfficeSource(source) && Boolean(onOpenPdfPassage)) ||
                        (isImageSource(source) && Boolean(onOpenImagePassage)) ||
                        (isTextLikeSource(source) && Boolean(onOpenTextSource)) ||
                        (Boolean(locator?.regions?.length) && Boolean(onOpenPdfPassage)) ||
                        // A PDF passage with only a section locator (no page)
                        // still opens: the viewer scans the pages for the quote.
                        (isPdfSource(source) &&
                          Boolean(locator?.excerpt ?? source.excerpt) &&
                          Boolean(onOpenPdfPassage))));
                  const isRowActive = activeCitation === citationId;
                  const quote = locator?.excerpt ?? source.excerpt ?? null;
                  const detailStr = locator ? locatorDetailStr(locator) : '';
                  const locLine = [
                    pageStr ? `${t('chat.sources.pageAbbr', { defaultValue: 'S.' })} ${pageStr}` : null,
                    sectionNum || null,
                    sectionTitle || null,
                    detailStr || null,
                  ]
                    .filter(Boolean)
                    .join(' \u00b7 ');
                  const mainLine = locLine || quote || t('chat.sources.noLocation', { defaultValue: '\u2013' });
                  // Every passage row shows its quote inline (hover-only
                  // quotes suppress verification, and quote-on-first-row-only
                  // read as "the other passages have no text").
                  const showInlineQuote = Boolean(quote) && Boolean(locLine);
                  const jumpLabel = t('chat.sources.locator.jump', {
                    defaultValue: 'Open the document at {{loc}}',
                    loc: locLine || displayTitle,
                  });

                  return (
                    <Tooltip
                      key={`chunk-${rowKey}`}
                      title={showInlineQuote ? '' : quote ?? ''}
                      placement="top"
                    >
                      <Box
                        id={isAnchor ? `source-card-${citationId}` : undefined}
                        role={rowCanJump ? 'button' : undefined}
                        tabIndex={rowCanJump ? 0 : undefined}
                        aria-label={rowCanJump ? jumpLabel : undefined}
                        onClick={
                          rowCanJump
                            ? () => handleDocumentClick(source, locator, rowLabel, locatorPassages(source, locator, rowLabel))
                            : undefined
                        }
                        onKeyDown={
                          rowCanJump
                            ? (e: React.KeyboardEvent) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleDocumentClick(source, locator, rowLabel, locatorPassages(source, locator, rowLabel));
                                }
                              }
                            : undefined
                        }
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.25,
                          px: 2,
                          py: '10px',
                          borderBottom: rowIdx < rows.length - 1 ? '1px solid #eaecf0' : 'none',
                          backgroundColor: isRowActive ? 'rgba(0,48,100,0.04)' : 'transparent',
                          transition: 'background-color 0.15s',
                          cursor: rowCanJump ? 'pointer' : 'default',
                          '&:hover': rowCanJump ? { backgroundColor: 'rgba(0,48,100,0.05)' } : {},
                          '&:focus-visible': { outline: '2px solid #003064', outlineOffset: '-2px' },
                        }}
                      >
                        {/* Passage index "2.1"/"2.2": couples each row to the
                            group's document number. List-only — the in-text
                            chips deliberately stay flat per document. */}
                        {docNumber && rows.length > 1 && (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              color: '#8593a5',
                              flexShrink: 0,
                              mt: '2px',
                              minWidth: '26px',
                            }}
                          >
                            {docNumber}.{rowIdx + 1}
                          </Typography>
                        )}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: '0.83rem',
                              fontWeight: locLine ? 600 : 400,
                              fontStyle: locLine ? 'normal' : 'italic',
                              color: rowCanJump ? '#0a5ad6' : '#3a4a5f',
                              lineHeight: 1.35,
                              overflow: 'hidden',
                              // Location labels stay one line; a quote-as-main
                              // row (text sources without page/section) clamps
                              // to two lines so it reads inside a narrow card.
                              ...(locLine
                                ? { textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                                : {
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                  }),
                            }}
                          >
                            {mainLine}
                          </Typography>
                          {showInlineQuote && (
                            <Typography
                              sx={{
                                fontSize: '0.78rem',
                                fontStyle: 'italic',
                                color: '#5a6a7d',
                                lineHeight: 1.4,
                                mt: '3px',
                                pl: 1,
                                borderLeft: '2px solid #c9d4e0',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {quote}
                            </Typography>
                          )}
                        </Box>
                        {rowCanJump && (
                          <LinkIcon sx={{ fontSize: '1rem', color: '#0a5ad6', flexShrink: 0, mt: '3px' }} />
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </Collapse>

      {/* FAQ Detail Dialog */}
      <FAQDetailDialog
        open={faqDialogOpen}
        onClose={() => {
          setFaqDialogOpen(false);
          setSelectedFaqId(null);
        }}
        faqId={selectedFaqId}
        isModerator={user?.role === 'admin' || user?.role === 'moderator'}
        currentUserId={user?.user_id ?? null}
      />
    </Box>
  );
};
